import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api.js'
import { cart } from '../lib/cart.js'
import { aud, optionUnit } from '../lib/format.js'
import { SITE, slugForCategory, isStudioPhoto } from '../data/site.js'
import { FINISH_SWATCHES } from '../data/finishes.js'
import { focusStyle, imageOption, imageStep } from '../lib/imageFocus.js'
import { isAddUp, addOn, addOnTotal } from '../lib/optionPricing.js'
import Icon from '../components/Icon.jsx'
import ProductImage from '../components/ProductImage.jsx'
import ProductCard from '../components/ProductCard.jsx'
import { relatedProducts } from '../lib/related.js'

// Sizes ("900", "740-820", "2100 x 1000mm") read better as a dropdown than as a wall of
// tiles — anything with words in it (colours, Left/Right, "2 Doors (W900-1800mm)") stays tiles.
const isMeasurement = (value) => /\d/.test(value) && /^[\d\s.,\-x×/]+(mm|cm|m)?$/i.test(String(value).trim())
const isMeasurementOption = (opt) => opt.values.length > 1 && opt.values.every(isMeasurement)
const unitSuffix = (opt, value) => optionUnit(opt.name, value)
const measurementOrder = (value) => (String(value).match(/\d+(?:[.,]\d+)?/g) || []).map(Number)
const sortMeasurements = (values) => [...values].sort((a, b) => {
  const [x, y] = [measurementOrder(a), measurementOrder(b)]
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) - (y[i] ?? 0)
  }
  return 0
})

export default function Product() {
  const { sku } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState({})
  // which configurator steps are expanded — a Set so choosing a value never collapses
  // the step you're on (you can keep comparing colours) while the next step still opens
  const [openSteps, setOpenSteps] = useState(() => new Set([0]))
  const [qty, setQty] = useState(1)
  const [imgIndex, setImgIndex] = useState(0)
  const [broken, setBroken] = useState(() => new Set())
  const [added, setAdded] = useState(false)
  const [zoom, setZoom] = useState(false)  // full-image lightbox
  const [catalogue, setCatalogue] = useState([])  // for the "goes well with" row

  useEffect(() => {
    setData(null); setSelected({}); setOpenSteps(new Set([0])); setQty(1); setImgIndex(0); setAdded(false); setBroken(new Set()); setZoom(false)
    api.product(sku).then((d) => {
      setData(d)
      // pre-select options that only have one value, and open the first real choice
      try {
        const opts = JSON.parse(d.product.options) || []
        const pre = {}
        opts.forEach((o) => { if (o.values.length === 1) pre[o.name] = o.values[0] })
        if (Object.keys(pre).length) {
          setSelected(pre)
          const firstOpen = opts.findIndex((o) => o.values.length > 1)
          setOpenSteps(new Set([firstOpen === -1 ? opts.length : firstOpen]))
        }
      } catch { /* options unreadable — leave manual flow */ }
    }).catch((e) => setError(e.message))
  }, [sku])

  // the whole published catalogue already comes from one cached endpoint — reuse it to
  // work out what goes with this product instead of adding another API
  useEffect(() => {
    api.products().then((d) => setCatalogue(d.products || [])).catch(() => setCatalogue([]))
  }, [])

  const product = data?.product
  const variations = data?.variations || []
  const options = useMemo(() => {
    if (!product) return []
    try { return JSON.parse(product.options) || [] } catch { return [] }
  }, [product])
  const allImages = useMemo(() => {
    if (!product) return []
    try { return JSON.parse(product.images) || [] } catch { return [] }
  }, [product])
  // drop photos the browser can't load (e.g. an upload missing from the database)
  const images = allImages.filter((src) => !broken.has(src))
  const markBroken = (src) => setBroken((b) => new Set(b).add(src))

  // on load, lead with the diagram of the step the customer starts on
  useEffect(() => {
    if (!product || !images.length || Object.keys(selected).length) return
    const first = [...openSteps][0]
    const name = (() => { try { return (JSON.parse(product.options) || [])[first]?.name } catch { return null } })()
    if (!name) return
    const idx = images.findIndex((src) => imageStep(src) === name)
    if (idx !== -1) setImgIndex(idx)
  }, [product?.sku, images.length]) // eslint-disable-line

  // lightbox: lock scroll and wire Esc / arrow keys while open
  useEffect(() => {
    if (!zoom) return
    const onKey = (e) => {
      if (e.key === 'Escape') setZoom(false)
      else if (e.key === 'ArrowRight' && images.length > 1) setImgIndex((i) => (i + 1) % images.length)
      else if (e.key === 'ArrowLeft' && images.length > 1) setImgIndex((i) => (i - 1 + images.length) % images.length)
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
  }, [zoom, images.length])

  const isVariable = variations.length > 0
  const enquireOnly = Boolean(product?.price_on_request)  // no price shown — customer calls or messages
  // add-up products price themselves from the parts chosen, so they carry no variations
  const addUp = isAddUp(options)
  // covers a simple product at 0 stock, and a variable product whose variations are all
  // out of stock (the API filters them out, so nothing is left to price) or never priced
  const soldOut = !enquireOnly && (
    product?.stock === 0 || (!isVariable && !addUp && product?.price == null)
  )
  const totalSteps = ((isVariable || addUp) ? options.length : 0) + 1 // option steps + quantity step

  const match = useMemo(() => {
    if (!isVariable) return null
    return variations.find((v) => {
      const o = JSON.parse(v.opts)
      return options.every((opt) => o[opt.name] === selected[opt.name])
    })
  }, [variations, options, selected, isVariable])

  const allChosen = options.every((opt) => selected[opt.name])
  // base price + every chosen part; the price only firms up once all parts are picked
  const partsTotal = addOnTotal(options, selected)
  const baseUnit = (product?.sale_price ?? product?.price) || 0
  const unit = addUp
    ? (allChosen ? baseUnit + partsTotal : null)
    : isVariable
      ? (match ? (match.sale_price ?? match.price) : null)
      : (product ? (product.sale_price ?? product.price) : null)
  const regular = addUp
    ? (allChosen ? (product?.price || 0) + partsTotal : null)
    : isVariable ? match?.price : product?.price
  const onSale = unit != null && regular != null && unit < regular

  // price hint per option value, holding other selections fixed
  const priceHint = (optName, value) => {
    if (addUp) return null  // add-up parts show their own "+$x" label instead
    const others = { ...selected, [optName]: value }
    const candidates = variations.filter((v) => {
      const o = JSON.parse(v.opts)
      return Object.entries(others).every(([k, val]) => !val || o[k] === val)
    })
    if (!candidates.length) return null
    const min = Math.min(...candidates.map((v) => v.sale_price ?? v.price))
    return min
  }

  if (error) {
    return (
      <main className="container page">
        <p className="muted">Product not found. <Link to="/shop">Back to shop</Link></p>
      </main>
    )
  }
  if (!product) return <main className="container page"><p className="muted">Loading…</p></main>

  const catSlug = slugForCategory(product.category)
  const related = relatedProducts(product, catalogue)

  function choose(optName, value, optIndex) {
    const next = { ...selected, [optName]: value }
    setSelected(next)
    setAdded(false)
    // jump the gallery to the photo of this value, if the owner tagged one
    const optIdx = images.findIndex((src) => imageOption(src) === value)
    if (optIdx !== -1) setImgIndex(optIdx)
    // keep this step open (the customer may still be comparing) AND reveal the next
    // step that still needs a choice — never collapse the one just picked from
    setOpenSteps((prev) => {
      const opened = new Set(prev)
      opened.add(optIndex)
      let target = options.length
      for (let j = optIndex + 1; j < options.length; j++) {
        if (!next[options[j].name]) { target = j; break }
      }
      opened.add(target)
      // the revealed step brings its own diagram forward, unless this pick had a photo
      if (optIdx === -1 && target < options.length) {
        const stepIdx = images.findIndex((src) => imageStep(src) === options[target].name)
        if (stepIdx !== -1) setImgIndex(stepIdx)
      }
      return opened
    })
  }

  function addToCart() {
    // add-up configurations have no variation row — key the cart line by the parts chosen
    const id = isVariable
      ? match.id
      : addUp ? `${product.sku}::${options.map((o) => selected[o.name]).join('|')}` : product.sku
    cart.add({
      id,
      productSku: product.sku,
      name: product.name,
      opts: isVariable ? JSON.parse(match.opts) : addUp ? { ...selected } : {},
      unitPrice: unit,
      qty,
      image: images[Math.min(imgIndex, images.length - 1)] || images[0] || '',
    })
    setAdded(true)
  }

  const stepDone = (i) => (i < options.length ? Boolean(selected[options[i].name]) : false)
  const isOpen = (i) => openSteps.has(i)
  // photo that illustrates a step (e.g. a diagram of which panel it sizes)
  const stepImageIndex = (i) => {
    const name = options[i]?.name
    return name ? images.findIndex((src) => imageStep(src) === name) : -1
  }
  const toggleStep = (i) => {
    setOpenSteps((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i); else next.add(i)
      return next
    })
    // opening a step shows its diagram, unless a chosen value already owns the gallery
    if (!isOpen(i)) {
      const chosen = selected[options[i]?.name]
      const valueIdx = chosen ? images.findIndex((src) => imageOption(src) === chosen) : -1
      const idx = valueIdx !== -1 ? valueIdx : stepImageIndex(i)
      if (idx !== -1) setImgIndex(idx)
    }
  }

  return (
    <main className="container page product-page">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <Link to="/">Home</Link> / <Link to="/shop">Shop</Link> /{' '}
        <Link to={`/shop/${catSlug}`}>{product.category}</Link> / <span>{product.name}</span>
      </nav>

      <div className="product-layout">
        <div className="product-gallery">
          <div
            className={`product-gallery-main ${images.length ? 'zoomable' : ''} ${isStudioPhoto(product) ? 'media-fit' : ''}`}
            onClick={() => { if (images.length) setZoom(true) }}
            role={images.length ? 'button' : undefined}
            tabIndex={images.length ? 0 : undefined}
            onKeyDown={(e) => { if (images.length && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setZoom(true) } }}
            aria-label={images.length ? 'View full image' : undefined}
          >
            <ProductImage
              src={images[Math.min(imgIndex, images.length - 1)]}
              category={product.category}
              alt={product.name}
              onFail={markBroken}
            />
            {onSale && <span className="badge badge-sale">Sale</span>}
            {images.length > 0 && <span className="gallery-zoom-hint" aria-hidden="true"><Icon name="search" size={17} /></span>}
          </div>
          {images.length > 1 && (
            <div className="product-gallery-thumbs">
              {images.map((src, i) => (
                <button
                  key={src + i}
                  className={i === imgIndex ? 'active' : ''}
                  onClick={() => setImgIndex(i)}
                  aria-label={`Photo ${i + 1}`}
                >
                  <img src={src} alt="" style={focusStyle(src)} onError={() => markBroken(src)} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="product-info">
          <span className="product-card-cat">{product.subcategory || product.category}</span>
          <h1>{product.name}</h1>
          <p className="product-short">{product.short_desc}</p>
          <div className="product-price-line">
            {enquireOnly ? (
              <strong className="price-enquire">Contact us for a price</strong>
            ) : unit != null ? (
              <>
                {onSale && <s>{aud(regular)}</s>}
                <strong className={onSale ? 'sale' : ''}>{aud(unit)}</strong>
                <span className="ex-gst">ex GST</span>
              </>
            ) : soldOut ? (
              <span className="muted">Currently out of stock</span>
            ) : (
              <span className="muted">Select options to see your price</span>
            )}
          </div>

          {enquireOnly && (
            <div className="enquire-box">
              <div className="enquire-head">
                <span className="enquire-icon"><Icon name="ruler" size={22} /></span>
                <div>
                  <strong>Made to measure — priced per job</strong>
                  <p>Tell us your sizes and we'll price it for you, usually the same business day.</p>
                </div>
              </div>
              <div className="enquire-actions">
                <a href={SITE.phoneHref} className="btn btn-primary btn-lg">
                  <Icon name="phone" size={17} /> Call {SITE.phone}
                </a>
                <Link to="/contact" className="btn btn-outline btn-lg">Send an enquiry</Link>
              </div>
              <div className="enquire-meta">
                <span><Icon name="clock" size={14} /> {SITE.hours}</span>
                <a href={`mailto:${SITE.email}`}><Icon name="mail" size={14} /> {SITE.email}</a>
              </div>
            </div>
          )}

          {!enquireOnly && (
          <div className="configurator" aria-label="Product configurator">
            {options.map((opt, i) => (
              <section key={opt.name} className={`config-step ${isOpen(i) ? 'open' : ''} ${stepDone(i) ? 'done' : ''}`}>
                <button className="config-step-head" onClick={() => toggleStep(i)} aria-expanded={isOpen(i)}>
                  <span className="config-step-num">{stepDone(i) ? <Icon name="check" size={15} /> : i + 1}</span>
                  <span className="config-step-title">
                    Step {i + 1}: Choose {opt.name.replace(/\s*\(mm\)/, '')}
                    {selected[opt.name] && <em> — {selected[opt.name]}{unitSuffix(opt, selected[opt.name])}</em>}
                  </span>
                  <Icon name="chevronDown" size={17} className={isOpen(i) ? 'flip' : ''} />
                </button>
                {isOpen(i) && (
                  <div className="config-step-body">
                    {isMeasurementOption(opt) ? (
                      <select
                        className="option-select"
                        value={selected[opt.name] || ''}
                        onChange={(e) => choose(opt.name, e.target.value, i)}
                        aria-label={`Choose ${opt.name}`}
                      >
                        <option value="" disabled>Select {opt.name.replace(/\s*\(mm\)/, '').toLowerCase()}…</option>
                        {sortMeasurements(opt.values).map((value) => {
                          const hint = priceHint(opt.name, value)
                          const part = addUp ? addOn(opt, value) : null
                          return (
                            <option key={value} value={value}>
                              {value}{unitSuffix(opt, value)}
                              {part != null ? (part ? ` — +${aud(part)}` : ' — included') : ''}
                              {hint != null ? ` — from ${aud(hint)}` : ''}
                            </option>
                          )
                        })}
                      </select>
                    ) : (
                      <div className={`option-grid ${FINISH_SWATCHES[opt.values[0]] ? 'option-grid-swatch' : ''}`}>
                        {opt.values.map((value) => {
                          const hint = priceHint(opt.name, value)
                          return (
                            <button
                              key={value}
                              className={`option-tile ${selected[opt.name] === value ? 'selected' : ''}`}
                              onClick={() => choose(opt.name, value, i)}
                            >
                              {FINISH_SWATCHES[value] && (
                                <span className="swatch" style={{ background: FINISH_SWATCHES[value] }} />
                              )}
                              <span className="option-label">{value}{unitSuffix(opt, value)}</span>
                              {addUp && (
                                <span className="option-price">
                                  {addOn(opt, value) ? `+${aud(addOn(opt, value))}` : 'included'}
                                </span>
                              )}
                              {hint != null && <span className="option-price">from {aud(hint)}</span>}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </section>
            ))}

            <section className={`config-step ${isOpen(options.length) ? 'open' : ''} ${added ? 'done' : ''}`}>
              <button className="config-step-head" onClick={() => toggleStep(options.length)} aria-expanded={isOpen(options.length)}>
                <span className="config-step-num">{added ? <Icon name="check" size={15} /> : options.length + 1}</span>
                <span className="config-step-title">Step {options.length + 1}: Quantity & add to cart</span>
                <Icon name="chevronDown" size={17} className={isOpen(options.length) ? 'flip' : ''} />
              </button>
              {isOpen(options.length) && (
                <div className="config-step-body">
                  {(isVariable || addUp) && !allChosen ? (
                    <p className="muted">Complete the steps above to unlock your price.</p>
                  ) : isVariable && !match ? (
                    <p className="muted">Sorry, this combination is currently out of stock — try a different option.</p>
                  ) : soldOut ? (
                    <p className="muted">Sorry, this item is currently out of stock. <Link to="/contact">Contact us</Link> for a restock date.</p>
                  ) : (
                    <>
                      <div className="qty-row">
                        <div className="qty-input">
                          <button onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease quantity">−</button>
                          <span aria-live="polite">{qty}</span>
                          <button onClick={() => setQty(qty + 1)} aria-label="Increase quantity">+</button>
                        </div>
                        <div className="qty-total">
                          <span>Total</span>
                          <strong>{aud(unit * qty)}</strong>
                          <span className="ex-gst">ex GST</span>
                        </div>
                      </div>
                      {added ? (
                        <div className="added-actions">
                          <p className="added-note"><Icon name="check" size={16} /> Added to cart</p>
                          <div className="hero-actions">
                            <button className="btn btn-primary" onClick={() => navigate('/cart')}>
                              Go to cart <Icon name="arrowRight" size={16} />
                            </button>
                            <button className="btn btn-outline" onClick={() => setAdded(false)}>Keep shopping</button>
                          </div>
                        </div>
                      ) : (
                        <button className="btn btn-primary btn-lg btn-block" onClick={addToCart}>
                          <Icon name="cart" size={18} /> Add to cart — {aud(unit * qty)}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </section>
          </div>
          )}

          <ul className="product-assurance">
            <li><Icon name="shield" size={16} /> Toughened safety glass · AS/NZS 2208</li>
            <li><Icon name="truck" size={16} /> Delivery within 50 km or free Ravenhall pickup</li>
            <li><Icon name="tag" size={16} /> 2-year hardware warranty</li>
          </ul>
        </div>
      </div>

      {related.length > 0 && (
        <section className="product-related">
          <div className="section-head">
            <h2>Goes well with</h2>
            <Link to={`/shop/${catSlug}`} className="link-arrow">
              More in {product.category} <Icon name="arrowRight" size={16} />
            </Link>
          </div>
          <div className="product-grid">
            {related.map((p) => <ProductCard key={p.sku} product={p} />)}
          </div>
        </section>
      )}

      {product.description && (
        <section className="product-description">
          <h2>Product details</h2>
          {product.description.split(/\n+/).map((para, i) => <p key={i}>{para}</p>)}
        </section>
      )}

      {zoom && images.length > 0 && (
        <div className="lightbox" role="dialog" aria-modal="true" onClick={() => setZoom(false)}>
          <button className="lightbox-close" onClick={() => setZoom(false)} aria-label="Close">
            <Icon name="x" size={22} />
          </button>
          {images.length > 1 && (
            <button
              className="lightbox-nav prev"
              onClick={(e) => { e.stopPropagation(); setImgIndex((imgIndex - 1 + images.length) % images.length) }}
              aria-label="Previous image"
            >
              <Icon name="chevronRight" size={26} className="flip" />
            </button>
          )}
          <img
            className="lightbox-img"
            src={images[Math.min(imgIndex, images.length - 1)]}
            alt={product.name}
            onClick={(e) => e.stopPropagation()}
          />
          {images.length > 1 && (
            <button
              className="lightbox-nav next"
              onClick={(e) => { e.stopPropagation(); setImgIndex((imgIndex + 1) % images.length) }}
              aria-label="Next image"
            >
              <Icon name="chevronRight" size={26} />
            </button>
          )}
        </div>
      )}
    </main>
  )
}
